import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Save, X, ArrowLeft, Upload, Undo, Redo, Download } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EditSalesBudgetFormTM } from './edit-sales-budget-form-tm';
import type { BudgetData, SalesBudget, SalesCategory } from '@/types/budget.types';
import { toast } from 'sonner';

interface EditSalesBudgetPageProps {
  budgetData: BudgetData;
  projectStartDate?: string;
  projectEndDate?: string;
  onSave: (updatedBudget: Partial<BudgetData>) => Promise<void>;
  onCancel: () => void;
}

// Mock available categories - in production, this would come from Settings → Budget Categories
// Updated to include test scenarios for delete validation:
// - Labour: Has actual costs (cannot delete)
// - Materials: Has budget only (can delete with warning)
// - Equipment: Has both budget and actuals (cannot delete)
// - Subcontractors: Has budget only (can delete with warning)
// - Travel & Expenses: Empty (can delete immediately)
// - Software & Licenses: Has actual costs (cannot delete)
// - Overhead: Empty (can delete immediately)
const MOCK_AVAILABLE_CATEGORIES: SalesCategory[] = [
  {
    id: 1,
    name: 'Labour',
    type: 'labour',
    budget: 350000,
    actual: 231000, // HAS ACTUALS - Cannot delete
    remaining: 119000,
    percentBilled: 66,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 2,
    name: 'Materials',
    type: 'expenses',
    budget: 104000, // NO ACTUALS - Can delete with warning
    actual: 0,
    remaining: 104000,
    percentBilled: 0,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 3,
    name: 'Equipment',
    type: 'expenses',
    budget: 78000,
    actual: 49400, // HAS ACTUALS - Cannot delete
    remaining: 28600,
    percentBilled: 63,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 4,
    name: 'Subcontractors',
    type: 'subcontractors',
    budget: 84000, // NO ACTUALS - Can delete with warning
    actual: 0,
    remaining: 84000,
    percentBilled: 0,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 5,
    name: 'Travel & Expenses',
    type: 'expenses',
    budget: 0, // NO BUDGET - Can delete immediately
    actual: 0,
    remaining: 0,
    percentBilled: 0,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 6,
    name: 'Software & Licenses',
    type: 'expenses',
    budget: 36400,
    actual: 23400, // HAS ACTUALS - Cannot delete
    remaining: 13000,
    percentBilled: 64,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 7,
    name: 'Overhead',
    type: 'expenses',
    budget: 0, // NO BUDGET - Can delete immediately
    actual: 0,
    remaining: 0,
    percentBilled: 0,
    scope: 'global',
    isCustom: false,
  },
  {
    id: 8,
    name: 'Contingency',
    type: 'expenses',
    budget: 19500,
    actual: 11700, // HAS ACTUALS - Cannot delete
    remaining: 7800,
    percentBilled: 60,
    scope: 'global',
    isCustom: false,
  },
];

export function EditSalesBudgetPage({
  budgetData,
  projectStartDate,
  projectEndDate,
  onSave,
  onCancel,
}: EditSalesBudgetPageProps) {
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [salesBudgetChanges, setSalesBudgetChanges] = useState<SalesBudget | null>(null);
  const [currentBudgetData, setCurrentBudgetData] = useState<BudgetData>(budgetData);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

  // Undo/Redo state
  const MAX_HISTORY = 30;
  const [history, setHistory] = useState<SalesBudget[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [undoRedoVersion, setUndoRedoVersion] = useState(0);
  const isUndoRedoInProgress = useRef(false);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const updatedBudget: Partial<BudgetData> = {};
      
      if (salesBudgetChanges) {
        updatedBudget.salesBudget = salesBudgetChanges;
      }

      await onSave(updatedBudget);
      
      // Reset state
      setIsDirty(false);
      setSalesBudgetChanges(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
    } else {
      onCancel();
    }
  };

  const handleCancel = () => {
    // Check if there are unsaved changes
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
    } else {
      onCancel();
    }
  };

  const handleConfirmLeave = () => {
    setShowUnsavedChangesDialog(false);
    onCancel();
  };

  // Undo handler
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoInProgress.current = true;
      
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      setHistoryIndex(newIndex);
      setSalesBudgetChanges(previousState);
      setCurrentBudgetData(prev => ({
        ...prev,
        salesBudget: previousState,
      }));
      setIsDirty(newIndex > 0);
      setUndoRedoVersion(v => v + 1);
      
      setTimeout(() => {
        isUndoRedoInProgress.current = false;
      }, 100);
    }
  }, [historyIndex, history]);

  // Redo handler
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoInProgress.current = true;
      
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      setHistoryIndex(newIndex);
      setSalesBudgetChanges(nextState);
      setCurrentBudgetData(prev => ({
        ...prev,
        salesBudget: nextState,
      }));
      setIsDirty(true);
      setUndoRedoVersion(v => v + 1);
      
      setTimeout(() => {
        isUndoRedoInProgress.current = false;
      }, 100);
    }
  }, [historyIndex, history]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.shiftKey && e.key === 'z' || e.key === 'y')) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Get available categories from the actual budget data (with monthlyBudgets from API)
  // Fall back to mock categories if no categories exist in the budget data
  // Deduplicate by name to prevent duplicate rows from appearing in the edit form
  const availableCategories = (() => {
    const cats = currentBudgetData.salesBudget?.categories && currentBudgetData.salesBudget.categories.length > 0
      ? currentBudgetData.salesBudget.categories
      : MOCK_AVAILABLE_CATEGORIES;
    const seen = new Set<string>();
    return cats.filter(cat => {
      if (seen.has(cat.name)) return false;
      seen.add(cat.name);
      return true;
    });
  })();

  return (
    <div className="min-h-screen bg-background flex flex-col rounded-lg">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                disabled={isSaving}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Edit Sales Budget</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Update sales budget values for {currentBudgetData.projectName}
                </p>
              </div>
              
              {/* Compact Unsaved Changes Indicator */}
              {isDirty && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-md text-amber-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">Unsaved changes</span>
                </div>
              )}
            </div>
            
            {/* Import and Export Buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => toast.info('Export feature coming soon')}
                disabled={isSaving}
                title="Export budget"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => toast.info('Import feature coming soon')}
                disabled={isSaving}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mt-3">
              <Alert variant="destructive" showIcon={false}>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-4">
          <EditSalesBudgetFormTM
            key={`sales-${undoRedoVersion}`}
            projectId={budgetData.projectId}
            salesBudget={currentBudgetData.salesBudget}
            projectStartDate={projectStartDate}
            projectEndDate={projectEndDate}
            availableCategories={availableCategories}
            resourceBudget={currentBudgetData.resourceBudget}
            onChange={(changes) => {
              setSalesBudgetChanges(changes);
              
              // Skip history updates during undo/redo operations
              if (isUndoRedoInProgress.current) {
                return;
              }
              
              // First onChange call - initialize history
              if (!hasUserInteracted) {
                setHasUserInteracted(true);
                setHistory([currentBudgetData.salesBudget]);
                setHistoryIndex(0);
                return;
              }
              
              // User has interacted - this is a real change
              setIsDirty(true);
              
              // Push to history for undo/redo
              const newHistory = history.slice(0, historyIndex + 1);
              newHistory.push(changes);
              
              // Limit history to MAX_HISTORY items
              if (newHistory.length > MAX_HISTORY) {
                newHistory.shift();
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
              } else {
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
              }
            }}
          />
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 z-10 bg-background border-t shadow-lg">
        <div className="px-4 py-3">
          <div className="flex justify-end items-center gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              size="lg"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            
            <Button
              variant="outline"
              onClick={handleUndo}
              disabled={historyIndex <= 0 || isSaving}
              size="lg"
              title="Undo (Ctrl+Z)"
            >
              <Undo className="h-4 w-4 mr-2" />
              Undo {historyIndex > 0 && `(${historyIndex})`}
            </Button>
            
            <Button
              variant="outline"
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1 || isSaving}
              size="lg"
              title="Redo (Ctrl+Shift+Z)"
            >
              <Redo className="h-4 w-4 mr-2" />
              Redo {historyIndex < history.length - 1 && `(${history.length - 1 - historyIndex})`}
            </Button>
            
            <Button
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              size="lg"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Budget'}
            </Button>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure that you want to leave the current page? The changes that you made won't be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLeave}>Leave</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
